import React, { useEffect, useRef, useState } from 'react';

interface Command {
  id: string;
  label: string;
  description: string;
  icon: string;
  action: () => void;
}

interface InvestorCommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (tabId: string) => void;
}

const InvestorCommandPalette: React.FC<InvestorCommandPaletteProps> = ({
  isOpen,
  onClose,
  onNavigate
}) => {
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const commands: Command[] = [
    {
      id: 'add-investor',
      label: 'Add investor',
      description: 'Create new investor record',
      icon: '➕',
      action: () => {
        onNavigate('investors');
        onClose();
        // Focus on quick add form after navigation
        setTimeout(() => {
          const quickAddInput = document.querySelector<HTMLInputElement>('input[placeholder*="Company name"]');
          quickAddInput?.focus();
        }, 100);
      }
    },
    {
      id: 'view-pipeline',
      label: 'View Pipeline',
      description: 'Switch to pipeline board view',
      icon: '📊',
      action: () => {
        onNavigate('pipeline');
      }
    },
    {
      id: 'view-investors',
      label: 'View Investors',
      description: 'Switch to investors list view',
      icon: '📋',
      action: () => {
        onNavigate('investors');
      }
    },
    {
      id: 'refresh-data',
      label: 'Refresh data',
      description: 'Reload investor data',
      icon: '🔄',
      action: () => {
        onClose();
        window.location.reload();
      }
    }
  ];

  const filteredCommands = commands.filter(cmd =>
    cmd.label.toLowerCase().includes(search.toLowerCase()) ||
    cmd.description.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      setSearch('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, filteredCommands.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const selectedCommand = filteredCommands[selectedIndex];
      if (selectedCommand) {
        selectedCommand.action();
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 pt-20">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl ring-1 ring-gray-100">
        {/* Search Input */}
        <div className="border-b border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <span className="text-xl">🔍</span>
            <input
              ref={inputRef}
              type="text"
              className="flex-1 border-none bg-transparent text-lg focus:outline-none"
              placeholder="Search commands..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <kbd className="rounded border border-gray-300 bg-gray-100 px-2 py-1 text-xs font-semibold">
              ESC
            </kbd>
          </div>
        </div>

        {/* Commands List */}
        <div className="max-h-96 overflow-y-auto p-2">
          {filteredCommands.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">
              No commands found
            </div>
          ) : (
            filteredCommands.map((command, index) => (
              <button
                key={command.id}
                onClick={() => {
                  command.action();
                  onClose();
                }}
                className={`flex w-full items-center gap-3 rounded-lg p-3 text-left transition ${
                  index === selectedIndex
                    ? 'bg-primary/10 text-primary'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="text-xl">{command.icon}</span>
                <div className="flex-1">
                  <div className="font-medium">{command.label}</div>
                  <div className="text-xs text-gray-500">{command.description}</div>
                </div>
                {index === selectedIndex && (
                  <kbd className="rounded border border-gray-300 bg-gray-100 px-2 py-1 text-xs font-semibold">
                    ↵
                  </kbd>
                )}
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-3">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center gap-2">
              <kbd className="rounded border border-gray-300 bg-gray-100 px-2 py-1">↑↓</kbd>
              <span>Navigate</span>
            </div>
            <div className="flex items-center gap-2">
              <kbd className="rounded border border-gray-300 bg-gray-100 px-2 py-1">↵</kbd>
              <span>Select</span>
            </div>
            <div className="flex items-center gap-2">
              <kbd className="rounded border border-gray-300 bg-gray-100 px-2 py-1">ESC</kbd>
              <span>Close</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestorCommandPalette;
